import { clsx } from "clsx"
import { FunnelCardWarning } from "components/display/FunnelCardWarning"
import { ChangeEventHandler, forwardRef } from "react"
import { FormField } from "../FormField"
import { Sizes } from "../NumberSelector"

export interface InputWithUnitProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  labelTooltip?: string
  unit: string
  placeholder?: string
  value?: string
  onChange?: ChangeEventHandler<HTMLInputElement>;
  variantSize?: Sizes
  variant?: 'form' | 'guarantee'
  warningMessage?: string
  error?: string;
  required?: boolean;
}

const getSizeClasses = (size: Sizes) => {
  const sizeMap = {
    [Sizes.xs]: 'w-20',
    [Sizes.sm]: 'w-32',
    [Sizes.base]: 'w-40',
    [Sizes.md]: 'w-48',
    [Sizes.lg]: 'w-56',
    [Sizes.xl]: 'w-64',
    [Sizes['2xl']]: 'w-72',
    [Sizes['3xl']]: 'w-80',
    [Sizes['4xl']]: 'w-96',
    [Sizes['5xl']]: 'w-full'
  }
  return sizeMap[size]
}

export const Input = forwardRef<HTMLInputElement, InputWithUnitProps>(
  function InputWithUnit(
    {
      label,
      unit,
      variantSize,
      variant = 'form',
      warningMessage,
      className,
      placeholder,
      error,
      ...props
    },
    ref
  ) {

    if (variant === 'guarantee') {
      const sizeClass = getSizeClasses(variantSize || Sizes.base);
      return (
        <div className={sizeClass}>
          {label && (
            <div className="flex items-center gap-1">
              <label htmlFor="price" className="block text-sm/6 text-primary font-medium">
                {label}
              </label>
              {warningMessage && (
                <FunnelCardWarning warningMessages={[warningMessage]} />
              )}
            </div>
          )}
          <div>
            <div className={clsx(
              "flex items-center rounded bg-white px-3 outline outline-1 -outline-offset-1 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2",
              error 
                ? "outline-red-500 focus-within:outline-red-500" 
                : "outline-gray-300 focus-within:outline-primary"
            )}>
              <input
                ref={ref}
                id="price"
                name="price"
                type="text"
                placeholder={placeholder}
                aria-describedby="price-currency"
                className={clsx(
                  "block min-w-0 grow py-1.5 pr-3 text-base placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6",
                  error ? "text-red-900" : "text-gray-900",
                  className
                )}
                {...props}
              />
              {unit && (
                <div id="price-currency" className="shrink-0 text-primary border-l pl-2 select-none text-base sm:text-sm/6">
                  {unit}
                </div>
              )}
            </div>
          </div>
        </div>
      );

    }

    return (
      <div>
        {label && (
          <div className="flex items-center gap-1">
            <label htmlFor="price" className="block text-sm/6 text-primary font-medium">
              {label}
            </label>
            {warningMessage && (
              <FunnelCardWarning warningMessages={[warningMessage]} />
            )}
          </div>
        )}
        <div>
          <div className={clsx(
            "flex items-center rounded bg-white pr-4 outline outline-1 -outline-offset-1 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2",
            error 
              ? "outline-red-500 focus-within:outline-red-500" 
              : "outline-gray-300 focus-within:outline-primary"
          )}>
            <input
              ref={ref}
              id="price"
              name="price"
              type="text"
              placeholder={placeholder}
              aria-describedby="price-currency"
              className={clsx(
                "px-4 py-4 w-full h-12 pr-4 text-base placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6",
                error ? "text-red-900" : "text-gray-900",
                className
              )}
              {...props}
            />
            {unit && (
              <div id="price-currency" className="shrink-0 text-primary border-l pl-4 select-none text-base sm:text-sm/6">
                {unit}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export const InputWithUnit = forwardRef<HTMLInputElement, InputWithUnitProps>(
  ({ label, labelTooltip, error, required, ...props }, ref) => {
    return (
      <FormField
        label={label}
        labelTooltip={labelTooltip}
        error={error}
        required={required}
        name={props.name || ''}
      >
        <Input ref={ref} error={error} {...props} />
      </FormField>
    );
  }
);
